/**
 * Chart color utilities
 *
 * Provides consistent color palettes for charts and visualizations
 * across the Planning feature.
 */

/**
 * Primary color palette for charts
 * Optimized for visibility and accessibility
 * Colors are vibrant and distinguishable
 */
export const CHART_COLORS = [
  '#3366CC',
  '#DC3912',
  '#FF9900',
  '#109618',
  '#990099',
  '#3B3EAC',
  '#0099C6',
  '#DD4477',
  '#66AA00',
  '#B82E2E',
  '#316395',
  '#994499',
  '#22AA99',
  '#AAAA11',
  '#6633CC',
  '#E67300',
  '#8B0707',
  '#329262',
  '#5574A6',
  '#651067',
  '#B749A2',
  '#4ED2C7',
  '#F77F4F',
  '#2E8B57',
  '#C15E9E',
  '#48C9A9',
  '#F1C40F',
  '#D35400',
  '#7D3C98',
  '#16A085',
]

/**
 * Gets a color from the palette by index
 * Cycles through colors if index exceeds palette size
 *
 * @param index - Index of the color to retrieve
 * @returns Hex color string
 */
export const getChartColor = (index: number): string => {
  return CHART_COLORS[index % CHART_COLORS.length]
}

/**
 * Generates an array of colors for a given number of items
 * Ensures colors are evenly distributed from the palette
 *
 * @param count - Number of colors needed
 * @returns Array of hex color strings
 */
export const generateChartColors = (count: number): string[] => {
  return Array.from({ length: count }, (_, i) => getChartColor(i))
}

/**
 * Creates a color map for named items (e.g., subjects)
 * Each unique name gets a consistent color from the palette
 *
 * @param names - Array of item names
 * @returns Map of name to color
 */
export const createColorMap = (names: string[]): Map<string, string> => {
  const uniqueNames = Array.from(new Set(names))
  const colorMap = new Map<string, string>()

  uniqueNames.forEach((name, index) => {
    colorMap.set(name, getChartColor(index))
  })

  return colorMap
}

/**
 * Alternative: Generate colors using HSL for more uniform distribution
 * Useful when you need many colors with good contrast
 *
 * @param count - Number of colors to generate
 * @param saturation - Saturation percentage (0-100)
 * @param lightness - Lightness percentage (0-100)
 * @returns Array of HSL color strings
 */
export const generateHSLColors = (count: number, saturation = 70, lightness = 60): string[] => {
  return Array.from({ length: count }, (_, i) => {
    const hue = (i * 360) / count
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`
  })
}
